/**
 * CrmWorkspaces - Página de gerenciamento de workspaces CRM
 * 
 * Permite criar, editar e gerenciar workspaces que organizam
 * os pipelines de vendas por área de negócio.
 * Inclui gerenciamento de pipelines e fases inline.
 * 
 * @author Sistema
 * @version 2.0.0
 */

import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Layers, 
  MoreVertical, 
  Edit, 
  Trash2,
  GitBranch,
  Briefcase,
  DollarSign,
  ChevronDown,
  ChevronRight,
  Settings2,
  Edit2,
  Webhook,
  Zap,
  Download,
  Upload,
  Shield,
  GripVertical
} from 'lucide-react';
// Drag and drop para reordenar pipelines (v2.1.0)
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { WorkspacePhasePermissionsDialog } from '@/components/crm/workspace/WorkspacePhasePermissionsDialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCrmWorkspacesStats, CrmWorkspaceWithStats } from '@/hooks/useCrmWorkspacesStats';
import { useCrmWorkspaceCategories } from '@/hooks/useCrmWorkspaceCategories';
import { WorkspaceCategorySelect } from '@/components/crm/WorkspaceCategorySelect';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { PipelinePhaseManager } from '@/components/crm/PipelinePhaseManager';
import { WorkspaceWebhookConfig } from '@/components/crm/WorkspaceWebhookConfig';
import { WorkspaceDistributionConfig } from '@/components/crm/WorkspaceDistributionConfig';
import { TemplateExportDialog } from '@/components/template-system/TemplateExportDialog';
import { buildWorkspaceSelection } from '@/lib/template-system/scopedSelection';
import type { TemplateCollectionItems } from '@/lib/template-system/api';
import { ImportWorkspaceDialog } from '@/components/crm/workspace/ImportWorkspaceDialog';
import { WorkspacePlaybooksDialog } from '@/components/crm/workspace/WorkspacePlaybooksDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { useDealPipelines } from '@/hooks/useDealPipelines';

// Logger
const logger = createLogger('CrmWorkspaces');

/**
 * Interface para pipeline com fases
 */
interface PipelineWithPhases {
  id: string;
  name: string;
  description: string | null;
  color: string | null; // v2.2.0: cor personalizada da aba (NULL = automática)
  is_default: boolean;
  workspace_id: string;
  sort_order: number;
  phases: {
    id: string;
    name: string;
    color: string | null;
    sort_order: number;
    is_won: boolean;
    is_lost: boolean;
  }[];
}

/**
 * Formata valor monetário para exibição
 */
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

/**
 * Paleta de cores predefinidas para pipelines (v2.2.0)
 * Mesma paleta usada nas abas do Kanban (DealPipelineTabs).
 */
const PIPELINE_PRESET_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

/**
 * Componente para exibir um item de pipeline
 */
function PipelineItem({ 
  pipeline, 
  onManagePhases, 
  onEdit, 
  onDelete,
  dragHandle
}: { 
  pipeline: PipelineWithPhases;
  onManagePhases: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** Alça de arraste opcional (v2.1.0) renderizada à esquerda do item */
  dragHandle?: React.ReactNode;
}) {
  const { t } = useTranslation('crm');
  
  return (
    <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 group hover:border-primary/30 transition-colors">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {dragHandle}
        <GitBranch className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {/* v2.2.0: bolinha da cor personalizada do pipeline (quando definida) */}
            {pipeline.color && (
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: pipeline.color }}
                title={pipeline.color}
              />
            )}
            <span className="font-medium text-sm truncate">{pipeline.name}</span>
            {pipeline.is_default && (
              <Badge variant="secondary" className="text-xs shrink-0">
                {t('pipelines.default', 'Padrão')}
              </Badge>
            )}
          </div>
          
          {/* Fases do Pipeline - representação visual */}
          {pipeline.phases && pipeline.phases.length > 0 && (
            <div className="flex items-center gap-1 mt-1.5">
              {pipeline.phases.slice(0, 5).map((phase, idx) => (
                <Fragment key={phase.id}>
                  <div 
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: phase.color || '#94A3B8' }}
                    title={phase.name}
                  />
                  {idx < Math.min(pipeline.phases.length - 1, 4) && (
                    <ChevronRight className="h-2.5 w-2.5 text-muted-foreground/40 shrink-0" />
                  )}
                </Fragment>
              ))}
              {pipeline.phases.length > 5 && (
                <span className="text-xs text-muted-foreground ml-1">
                  +{pipeline.phases.length - 5}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Ações - nota: PipelineItem é componente interno sem acesso direto a permissões,
          as ações são condicionadas no nível do componente pai via props */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-7 w-7"
          onClick={onManagePhases}
          title={t('pipelines.managePhases', 'Gerenciar Fases')}
        >
          <Settings2 className="h-3.5 w-3.5" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-7 w-7"
          onClick={onEdit}
          title={t('common.edit', 'Editar')}
        >
          <Edit2 className="h-3.5 w-3.5" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={onDelete}
          title={t('common.delete', 'Excluir')}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Item de pipeline sortable (v2.1.0)
 * Envolve o `PipelineItem` com o comportamento de arraste do dnd-kit.
 */
function SortablePipelineItem({
  pipeline,
  onManagePhases,
  onEdit,
  onDelete,
  canReorder,
}: {
  pipeline: PipelineWithPhases;
  onManagePhases: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canReorder: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pipeline.id, disabled: !canReorder });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <PipelineItem
        pipeline={pipeline}
        onManagePhases={onManagePhases}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandle={
          canReorder ? (
            <button
              type="button"
              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0"
              title="Arraste para reordenar"
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
            </button>
          ) : undefined
        }
      />
    </div>
  );
}

/**
 * Lista de pipelines de um workspace com suporte a reordenação (v2.1.0)
 * Mantém ordem local para feedback imediato e persiste via RPC segura.
 */
function WorkspacePipelinesList({
  workspaceId,
  pipelines,
  canReorder,
  onManagePhases,
  onEdit,
  onDelete,
}: {
  workspaceId: string;
  pipelines: PipelineWithPhases[];
  canReorder: boolean;
  onManagePhases: (pipeline: PipelineWithPhases) => void;
  onEdit: (pipeline: PipelineWithPhases) => void;
  onDelete: (pipeline: PipelineWithPhases) => void;
}) {
  const { reorderPipelines } = useDealPipelines();
  const [ordered, setOrdered] = useState<PipelineWithPhases[]>(pipelines);

  // Sincroniza com os dados vindos do servidor
  useEffect(() => {
    setOrdered(pipelines);
  }, [pipelines]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Aplica e persiste a nova ordem
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = ordered.findIndex(p => p.id === active.id);
    const newIndex = ordered.findIndex(p => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const anterior = ordered;
    const nova = arrayMove(ordered, oldIndex, newIndex);
    setOrdered(nova);

    try {
      await reorderPipelines.mutateAsync({
        workspaceId,
        pipelineIds: nova.map(p => p.id),
      });
    } catch {
      // Reverte em caso de erro (toast exibido pela mutation)
      setOrdered(anterior);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ordered.map(p => p.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {ordered.map(pipeline => (
            <SortablePipelineItem
              key={pipeline.id}
              pipeline={pipeline}
              canReorder={canReorder}
              onManagePhases={() => onManagePhases(pipeline)}
              onEdit={() => onEdit(pipeline)}
              onDelete={() => onDelete(pipeline)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}



export default function CrmWorkspaces() {
  const { t } = useTranslation('crm');
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY = 'crm_workspaces';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);
  
  // === Estados de Workspace ===
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<CrmWorkspaceWithStats | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    categoryId: '' // ID da categoria (obrigatório)
  });

  // === Estados de Pipeline ===
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<string[]>([]);
  const [selectedPipelineForPhases, setSelectedPipelineForPhases] = useState<PipelineWithPhases | null>(null);
  const [isPhasesDialogOpen, setIsPhasesDialogOpen] = useState(false);
  const [isCreatePipelineDialogOpen, setIsCreatePipelineDialogOpen] = useState(false);
  const [isEditPipelineDialogOpen, setIsEditPipelineDialogOpen] = useState(false);
  const [isDeletePipelineDialogOpen, setIsDeletePipelineDialogOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState<PipelineWithPhases | null>(null);
  const [targetWorkspaceId, setTargetWorkspaceId] = useState<string | null>(null);
  const [pipelineFormData, setPipelineFormData] = useState({
    name: '',
    description: '',
    color: PIPELINE_PRESET_COLORS[0] // v2.2.0: cor da aba (padrão = primeira da paleta)
  });

  // === Estados de Webhook ===
  const [isWebhookDialogOpen, setIsWebhookDialogOpen] = useState(false);
  const [webhookWorkspace, setWebhookWorkspace] = useState<CrmWorkspaceWithStats | null>(null);

  // === Estados de Distribuição ===
  const [isDistributionDialogOpen, setIsDistributionDialogOpen] = useState(false);
  const [distributionWorkspace, setDistributionWorkspace] = useState<CrmWorkspaceWithStats | null>(null);

  // === Estados de Exportação/Importação ===
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportWorkspaceData, setExportWorkspaceData] = useState<CrmWorkspaceWithStats | null>(null);

  /** Módulo usado pelo atalho "exportar esta workspace". */
  const exportSelectionModules = useMemo(() => ['crm' as const], []);

  /** Seleção do assistente com a workspace e tudo que está vinculado a ela. */
  const buildExportSelection = useCallback(
    (itemsByModule: Record<string, TemplateCollectionItems[]>) =>
      buildWorkspaceSelection(exportWorkspaceData?.name ?? '', itemsByModule),
    [exportWorkspaceData],
  );
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

  // === Estados de Permissões por Fase ===
  // Guardamos apenas o ID para que o objeto seja sempre derivado da query
  // mais recente (`workspaces`), evitando snapshots congelados que não
  // refletem updates após mutations (ex.: toggle de permissões/playbook).
  const [permissionsWorkspaceId, setPermissionsWorkspaceId] = useState<string | null>(null);
  const [isPermissionsDialogOpen, setIsPermissionsDialogOpen] = useState(false);

  // === Estados de Playbooks (modal acessível pelo menu três pontos) ===
  const [playbooksWorkspaceId, setPlaybooksWorkspaceId] = useState<string | null>(null);
  const [isPlaybooksDialogOpen, setIsPlaybooksDialogOpen] = useState(false);

  /**
   * Abre o modal de Playbooks de Vendas para um workspace
   */
  const handleOpenPlaybooks = (workspace: CrmWorkspaceWithStats) => {
    setPlaybooksWorkspaceId(workspace.id);
    setIsPlaybooksDialogOpen(true);
    // Garante que os pipelines do workspace sejam carregados (query depende do expandedWorkspaces)
    setExpandedWorkspaces((prev) =>
      prev.includes(workspace.id) ? prev : [...prev, workspace.id],
    );
  };

  /**
   * Abre o dialog de configuração de webhook para um workspace
   */
  const handleOpenWebhook = (workspace: CrmWorkspaceWithStats) => {
    setWebhookWorkspace(workspace);
    setIsWebhookDialogOpen(true);
  };

  /**
   * Abre o dialog de configuração de distribuição para um workspace
   */
  const handleOpenDistribution = (workspace: CrmWorkspaceWithStats) => {
    setDistributionWorkspace(workspace);
    setIsDistributionDialogOpen(true);
  };

  /**
   * Abre o modal de Permissões por Fase para um workspace
   */
  const handleOpenPermissions = (workspace: CrmWorkspaceWithStats) => {
    setPermissionsWorkspaceId(workspace.id);
    setIsPermissionsDialogOpen(true);
  };

  // Query para buscar workspaces com estatísticas via RPC (arquitetura enterprise)
  const { data: workspaces, isLoading } = useCrmWorkspacesStats();

  // Deriva os workspaces selecionados a partir da lista atual da query.
  // Assim, após uma mutation invalidar `crm-workspaces-stats`, os modais
  // recebem o objeto atualizado automaticamente (sem F5).
  const permissionsWorkspace = permissionsWorkspaceId
    ? workspaces?.find((w) => w.id === permissionsWorkspaceId) ?? null
    : null;
  const playbooksWorkspace = playbooksWorkspaceId
    ? workspaces?.find((w) => w.id === playbooksWorkspaceId) ?? null
    : null;

  // Query para buscar pipelines dos workspaces expandidos
  const { data: pipelinesMap } = useQuery({
    queryKey: ['workspace-pipelines', currentTenant?.id, expandedWorkspaces],
    queryFn: async () => {
      if (!currentTenant?.id || expandedWorkspaces.length === 0) return {};
      
      const { data, error } = await supabase
        .from('crm_pipelines')
        .select(`
          id, 
          name, 
          description, 
          color,
          is_default, 
          workspace_id, 
          sort_order,
          phases:crm_pipeline_phases(id, name, color, sort_order, is_won, is_lost)
        `)
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .in('workspace_id', expandedWorkspaces)
        .order('sort_order');
      
      if (error) throw error;
      
      // Agrupa por workspace_id
      const grouped: Record<string, PipelineWithPhases[]> = {};
      (data || []).forEach((pipeline: any) => {
        // Ordena as fases por sort_order
        if (pipeline.phases) {
          pipeline.phases.sort((a: any, b: any) => a.sort_order - b.sort_order);
        }
        
        if (!grouped[pipeline.workspace_id]) {
          grouped[pipeline.workspace_id] = [];
        }
        grouped[pipeline.workspace_id].push(pipeline);
      });
      
      return grouped;
    },
    enabled: !!currentTenant?.id && expandedWorkspaces.length > 0
  });

  // === Mutations de Workspace ===
  
  // Mutation para criar workspace
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; color: string; categoryId: string }) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');
      
      const { data: result, error } = await supabase
        .from('crm_workspaces')
        .insert({
          tenant_id: currentTenant.id,
          name: data.name,
          description: data.description || null,
          color: data.color,
          category_id: data.categoryId
        })
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      setIsCreateDialogOpen(false);
      setFormData({ name: '', description: '', color: '#3B82F6', categoryId: '' });
      toast.success(t('workspaces.created', 'Workspace criado com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao criar workspace', error);
      toast.error(error.message || t('workspaces.createError', 'Erro ao criar workspace'));
    }
  });

  // Mutation para atualizar workspace
  const updateMutation = useMutation({
    mutationFn: async (data: { id: string; name: string; description: string; color: string; categoryId: string }) => {
      const { data: result, error } = await supabase
        .from('crm_workspaces')
        .update({
          name: data.name,
          description: data.description || null,
          color: data.color,
          category_id: data.categoryId
        })
        .eq('id', data.id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      setIsEditDialogOpen(false);
      setSelectedWorkspace(null);
      toast.success(t('workspaces.updated', 'Workspace atualizado com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao atualizar workspace', error);
      toast.error(error.message || t('workspaces.updateError', 'Erro ao atualizar workspace'));
    }
  });

  // Mutation para deletar workspace
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_workspaces')
        .update({ is_active: false })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      setIsDeleteDialogOpen(false);
      setSelectedWorkspace(null);
      toast.success(t('workspaces.deleted', 'Workspace removido com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao deletar workspace', error);
      toast.error(error.message || t('workspaces.deleteError', 'Erro ao remover workspace'));
    }
  });

  // === Mutations de Pipeline ===
  
  // Mutation para criar pipeline
  const createPipelineMutation = useMutation({
    mutationFn: async (data: { name: string; description: string; color?: string; workspaceId: string }) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');

      // Busca maior sort_order atual do workspace
      const { data: maxOrder } = await supabase
        .from('crm_pipelines')
        .select('sort_order')
        .eq('tenant_id', currentTenant.id)
        .eq('workspace_id', data.workspaceId)
        .order('sort_order', { ascending: false })
        .limit(1)
        .maybeSingle();

      const nextOrder = (maxOrder?.sort_order ?? -1) + 1;

      // Cria pipeline
      const { data: pipeline, error } = await supabase
        .from('crm_pipelines')
        .insert({
          tenant_id: currentTenant.id,
          workspace_id: data.workspaceId,
          name: data.name,
          description: data.description || null,
          color: data.color || null, // v2.2.0: cor escolhida (NULL = automática)
          sort_order: nextOrder,
          is_active: true,
          is_default: false,
        })
        .select()
        .single();

      if (error) throw error;

      // Cria fases padrão para o novo pipeline
      const defaultPhases = [
        { name: 'Novo Lead', color: '#3b82f6', sort_order: 0, probability_percent: 10 },
        { name: 'Qualificação', color: '#f59e0b', sort_order: 1, probability_percent: 30 },
        { name: 'Proposta', color: '#8b5cf6', sort_order: 2, probability_percent: 60 },
        { name: 'Negociação', color: '#06b6d4', sort_order: 3, probability_percent: 80 },
        { name: 'Ganho', color: '#10b981', sort_order: 4, probability_percent: 100, is_won: true },
        { name: 'Perdido', color: '#ef4444', sort_order: 5, probability_percent: 0, is_lost: true },
      ];

      await supabase
        .from('crm_pipeline_phases')
        .insert(
          defaultPhases.map(phase => ({
            ...phase,
            pipeline_id: pipeline.id,
            tenant_id: currentTenant.id,
          }))
        );

      return pipeline;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
      setIsCreatePipelineDialogOpen(false);
      setPipelineFormData({ name: '', description: '', color: PIPELINE_PRESET_COLORS[0] });
      setTargetWorkspaceId(null);
      toast.success(t('pipelines.created', 'Pipeline criado com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao criar pipeline', error);
      toast.error(error.message || t('pipelines.createError', 'Erro ao criar pipeline'));
    }
  });

  // Mutation para atualizar pipeline
  const updatePipelineMutation = useMutation({
    mutationFn: async (data: { id: string; name: string; description: string; color?: string }) => {
      const { data: result, error } = await supabase
        .from('crm_pipelines')
        .update({
          name: data.name,
          description: data.description || null,
          color: data.color || null // v2.2.0: cor escolhida (NULL = automática)
        })
        .eq('id', data.id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
      setIsEditPipelineDialogOpen(false);
      setSelectedPipeline(null);
      toast.success(t('pipelines.updated', 'Pipeline atualizado com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao atualizar pipeline', error);
      toast.error(error.message || t('pipelines.updateError', 'Erro ao atualizar pipeline'));
    }
  });

  // Mutation para deletar pipeline
  const deletePipelineMutation = useMutation({
    mutationFn: async (id: string) => {
      // Verifica se é o pipeline padrão
      const { data: pipeline } = await supabase
        .from('crm_pipelines')
        .select('is_default')
        .eq('id', id)
        .single();

      if (pipeline?.is_default) {
        throw new Error('Não é possível excluir o pipeline padrão');
      }

      // Soft delete
      const { error } = await supabase
        .from('crm_pipelines')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
      setIsDeletePipelineDialogOpen(false);
      setSelectedPipeline(null);
      toast.success(t('pipelines.deleted', 'Pipeline removido com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao deletar pipeline', error);
      toast.error(error.message || t('pipelines.deleteError', 'Erro ao remover pipeline'));
    }
  });

  // === Handlers de Workspace ===
  
  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error(t('workspaces.nameRequired', 'Nome é obrigatório'));
      return;
    }
    if (!formData.categoryId) {
      toast.error(t('workspaces.categoryRequired', 'Categoria é obrigatória'));
      return;
    }
    createMutation.mutate(formData);
  };

  const handleEdit = (workspace: CrmWorkspaceWithStats) => {
    setSelectedWorkspace(workspace);
    setFormData({
      name: workspace.name,
      description: workspace.description || '',
      color: workspace.color,
      categoryId: workspace.category_id
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedWorkspace || !formData.name.trim()) return;
    updateMutation.mutate({
      id: selectedWorkspace.id,
      ...formData
    });
  };

  const handleDelete = (workspace: CrmWorkspaceWithStats) => {
    setSelectedWorkspace(workspace);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedWorkspace) return;
    deleteMutation.mutate(selectedWorkspace.id);
  };

  // === Handlers de Pipeline ===
  
  const toggleWorkspace = (workspaceId: string) => {
    setExpandedWorkspaces(prev => 
      prev.includes(workspaceId) 
        ? prev.filter(id => id !== workspaceId)
        : [...prev, workspaceId]
    );
  };

  const handleCreatePipeline = (workspaceId: string) => {
    setTargetWorkspaceId(workspaceId);
    setPipelineFormData({ name: '', description: '', color: PIPELINE_PRESET_COLORS[0] });
    setIsCreatePipelineDialogOpen(true);
  };

  const handleEditPipeline = (pipeline: PipelineWithPhases) => {
    setSelectedPipeline(pipeline);
    setPipelineFormData({
      name: pipeline.name,
      description: pipeline.description || '',
      // v2.2.0: usa cor salva; se NULL (automática), sugere a primeira da paleta
      color: pipeline.color || PIPELINE_PRESET_COLORS[0]
    });
    setIsEditPipelineDialogOpen(true);
  };

  const handleDeletePipeline = (pipeline: PipelineWithPhases) => {
    setSelectedPipeline(pipeline);
    setIsDeletePipelineDialogOpen(true);
  };

  const handleManagePhases = (pipeline: PipelineWithPhases) => {
    setSelectedPipelineForPhases(pipeline);
    setIsPhasesDialogOpen(true);
  };

  const confirmCreatePipeline = () => {
    if (!pipelineFormData.name.trim() || !targetWorkspaceId) {
      toast.error(t('pipelines.nameRequired', 'Nome é obrigatório'));
      return;
    }
    createPipelineMutation.mutate({
      name: pipelineFormData.name,
      description: pipelineFormData.description,
      color: pipelineFormData.color,
      workspaceId: targetWorkspaceId
    });
  };

  const confirmUpdatePipeline = () => {
    if (!selectedPipeline || !pipelineFormData.name.trim()) return;
    updatePipelineMutation.mutate({
      id: selectedPipeline.id,
      name: pipelineFormData.name,
      description: pipelineFormData.description,
      color: pipelineFormData.color
    });
  };

  const confirmDeletePipeline = () => {
    if (!selectedPipeline) return;
    deletePipelineMutation.mutate(selectedPipeline.id);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Cabeçalho — padrão compacto */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">{t('workspaces.title', 'Workspaces')}</h1>
        </div>
        <div className="flex items-center gap-2">
          {hasCreatePermission && (
            <Button 
              variant="outline"
              size="xs"
              onClick={() => setIsImportDialogOpen(true)}
              className="shrink-0"
            >
              <Upload className="mr-1.5" />
              Importar
            </Button>
          )}
          {hasCreatePermission && (
            <Button 
              size="xs"
              onClick={() => setIsCreateDialogOpen(true)}
              className="shrink-0"
            >
              <Plus className="mr-1.5" />
              {t('workspaces.create', 'Novo Workspace')}
            </Button>
          )}
        </div>
      </div>

      {/* Lista de Workspaces */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-4 w-48" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : workspaces && workspaces.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {workspaces.map(workspace => (
            <Card key={workspace.id} className="group relative flex flex-col">
              {/* Barra lateral colorida */}
              <div 
                className="absolute top-0 left-0 w-1 h-full rounded-l-lg"
                style={{ backgroundColor: workspace.color }}
              />
              
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                <div className="space-y-1 min-w-0 flex-1">
                <CardTitle className="flex items-center gap-2">
                    {/* Ícone dinâmico da categoria do workspace */}
                    <i 
                      className={`${workspace.category_icon} text-base shrink-0`}
                      style={{ color: workspace.color }}
                      title={workspace.category_name}
                    />
                    <span className="truncate">{workspace.name}</span>
                  </CardTitle>
                  {workspace.description && (
                    <CardDescription className="line-clamp-2">
                      {workspace.description}
                    </CardDescription>
                  )}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {hasEditPermission && (
                      <DropdownMenuItem onClick={() => handleEdit(workspace)}>
                        <Edit className="h-4 w-4 mr-2" />
                        {t('common.edit', 'Editar')}
                      </DropdownMenuItem>
                    )}
                    {hasCreatePermission && (
                      <DropdownMenuItem onClick={() => handleCreatePipeline(workspace.id)}>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('pipelines.create', 'Novo Pipeline')}
                      </DropdownMenuItem>
                    )}
                    {hasEditPermission && (
                      <DropdownMenuItem onClick={() => handleOpenWebhook(workspace)}>
                        <Webhook className="h-4 w-4 mr-2" />
                        Webhook de Integração
                      </DropdownMenuItem>
                    )}
                    {hasEditPermission && (
                      <DropdownMenuItem onClick={() => handleOpenDistribution(workspace)}>
                        <Zap className="h-4 w-4 mr-2" />
                        Distribuição Automática
                      </DropdownMenuItem>
                    )}
                    {hasEditPermission && (
                      <DropdownMenuItem onClick={() => handleOpenPlaybooks(workspace)}>
                        <Zap className="h-4 w-4 mr-2" />
                        Playbook de Vendas
                        {workspace.playbook_mode_enabled && (
                          <span className="ml-2 h-1.5 w-1.5 rounded-full bg-primary" />
                        )}
                      </DropdownMenuItem>
                    )}
                    {hasEditPermission && (
                      <DropdownMenuItem onClick={() => handleOpenPermissions(workspace)}>
                        <Shield className="h-4 w-4 mr-2" />
                        Permissões por Fase
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => {
                      setExportWorkspaceData(workspace);
                      setIsExportDialogOpen(true);
                    }}>
                      <Download className="h-4 w-4 mr-2" />
                      Exportar Workspace
                    </DropdownMenuItem>
                    {hasDeletePermission && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => handleDelete(workspace)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('common.delete', 'Excluir')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              
              <CardContent className="pb-3">
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <GitBranch className="h-4 w-4" />
                    {workspace.pipelines_count} {t('workspaces.pipelines', 'pipelines')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-4 w-4" />
                    {workspace.deals_count} {t('workspaces.deals', 'negócios')}
                  </span>
                  {workspace.total_value > 0 && (
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <DollarSign className="h-4 w-4" />
                      {formatCurrency(workspace.total_value)}
                    </span>
                  )}
                </div>
              </CardContent>

              {/* Collapsible de Pipelines */}
              <Collapsible 
                open={expandedWorkspaces.includes(workspace.id)}
                onOpenChange={() => toggleWorkspace(workspace.id)}
                className="border-t"
              >
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="w-full justify-between rounded-t-none h-10 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <span className="flex items-center gap-2">
                      <GitBranch className="h-3.5 w-3.5" />
                      {t('workspaces.viewDetails', 'Ver detalhes do workspace')}
                    </span>
                    <ChevronDown className={cn(
                      "h-4 w-4 transition-transform duration-200",
                      expandedWorkspaces.includes(workspace.id) && "rotate-180"
                    )} />
                  </Button>
                </CollapsibleTrigger>
                
                <CollapsibleContent className="p-3 pt-0 space-y-2">
                  {/* Lista de pipelines do workspace */}
                  {pipelinesMap?.[workspace.id] && pipelinesMap[workspace.id].length > 0 && (
                    <WorkspacePipelinesList
                      workspaceId={workspace.id}
                      pipelines={pipelinesMap[workspace.id]}
                      canReorder={hasEditPermission}
                      onManagePhases={handleManagePhases}
                      onEdit={handleEditPipeline}
                      onDelete={handleDeletePipeline}
                    />
                  )}


                  {(!pipelinesMap?.[workspace.id] || pipelinesMap[workspace.id].length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {t('workspaces.noPipelines', 'Nenhum pipeline neste workspace')}
                    </p>
                  )}

                  {hasCreatePermission && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => handleCreatePipeline(workspace.id)}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" />
                      {t('pipelines.create', 'Novo Pipeline')}
                    </Button>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Layers className="h-12 w-12 text-muted-foreground/20 mb-4" />
            <h3 className="text-lg font-medium">
              {t('workspaces.empty', 'Nenhum workspace criado')}
            </h3>
            <p className="text-muted-foreground text-center max-w-sm mt-1">
              {t('workspaces.emptyDescription', 'Crie seu primeiro workspace para organizar seus pipelines de vendas por área de negócio.')}
            </p>
            {hasCreatePermission && (
              <Button 
                className="mt-4" 
                onClick={() => setIsCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('workspaces.createFirst', 'Criar Primeiro Workspace')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* ============ DIALOGS DE WORKSPACE ============ */}
      
      {/* Dialog de Criação de Workspace */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspaces.createTitle', 'Novo Workspace')}</DialogTitle>
            <DialogDescription>
              {t('workspaces.createDescription', 'Crie um workspace para organizar seus pipelines de vendas.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Campo de Categoria (primeiro e obrigatório) */}
            <WorkspaceCategorySelect
              value={formData.categoryId}
              onChange={(categoryId) => setFormData(prev => ({ ...prev, categoryId }))}
              error={!formData.categoryId && createMutation.isError}
              errorMessage={t('workspaces.categoryRequired', 'Categoria é obrigatória')}
            />
            
            <div className="space-y-2">
              <Label htmlFor="name">{t('workspaces.name', 'Nome')} <span className="text-destructive">*</span></Label>
              <Input
                id="name"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('workspaces.namePlaceholder', 'Ex: Energia Solar')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('workspaces.description', 'Descrição')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('workspaces.descriptionPlaceholder', 'Descreva o propósito deste workspace...')}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="color">{t('workspaces.color', 'Cor')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="flex-1"
                  placeholder="#3B82F6"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? t('common.creating', 'Criando...') : t('common.create', 'Criar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Edição de Workspace */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('workspaces.editTitle', 'Editar Workspace')}</DialogTitle>
            <DialogDescription>
              {t('workspaces.editDescription', 'Atualize as informações do workspace.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Campo de Categoria (primeiro e obrigatório) */}
            <WorkspaceCategorySelect
              value={formData.categoryId}
              onChange={(categoryId) => setFormData(prev => ({ ...prev, categoryId }))}
            />
            
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t('workspaces.name', 'Nome')} <span className="text-destructive">*</span></Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">{t('workspaces.description', 'Descrição')}</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-color">{t('workspaces.color', 'Cor')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="edit-color"
                  type="color"
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-12 h-10 p-1 cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={e => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="flex-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('common.saving', 'Salvando...') : t('common.save', 'Salvar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão de Workspace */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workspaces.deleteTitle', 'Excluir Workspace')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('workspaces.deleteDescription', 'Tem certeza que deseja excluir o workspace "{{name}}"? Esta ação não pode ser desfeita e todos os pipelines e negócios associados serão removidos.', { name: selectedWorkspace?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t('common.deleting', 'Excluindo...') : t('common.delete', 'Excluir')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ============ DIALOGS DE PIPELINE ============ */}
      
      {/* Dialog de Criação de Pipeline */}
      <Dialog open={isCreatePipelineDialogOpen} onOpenChange={setIsCreatePipelineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pipelines.createTitle', 'Novo Pipeline')}</DialogTitle>
            <DialogDescription>
              {t('pipelines.createDescription', 'Crie um novo pipeline de vendas.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="pipeline-name">{t('pipelines.name', 'Nome')}</Label>
              <Input
                id="pipeline-name"
                value={pipelineFormData.name}
                onChange={e => setPipelineFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('pipelines.namePlaceholder', 'Ex: Vendas B2B')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pipeline-description">{t('pipelines.description', 'Descrição')}</Label>
              <Textarea
                id="pipeline-description"
                value={pipelineFormData.description}
                onChange={e => setPipelineFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('pipelines.descriptionPlaceholder', 'Descreva o propósito deste pipeline...')}
                rows={3}
              />
            </div>
            {/* v2.2.0: seleção de cor da aba do pipeline (mesma paleta do Kanban) */}
            <div className="space-y-2">
              <Label>{t('pipelines.color', 'Cor')}</Label>
              <div className="flex flex-wrap gap-2">
                {PIPELINE_PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      pipelineFormData.color === color && 'ring-2 ring-offset-2 ring-primary'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setPipelineFormData(prev => ({ ...prev, color }))}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreatePipelineDialogOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={confirmCreatePipeline} disabled={createPipelineMutation.isPending}>
              {createPipelineMutation.isPending ? t('common.creating', 'Criando...') : t('common.create', 'Criar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Edição de Pipeline */}
      <Dialog open={isEditPipelineDialogOpen} onOpenChange={setIsEditPipelineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pipelines.editTitle', 'Editar Pipeline')}</DialogTitle>
            <DialogDescription>
              {t('pipelines.editDescription', 'Atualize as informações do pipeline.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-pipeline-name">{t('pipelines.name', 'Nome')}</Label>
              <Input
                id="edit-pipeline-name"
                value={pipelineFormData.name}
                onChange={e => setPipelineFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-pipeline-description">{t('pipelines.description', 'Descrição')}</Label>
              <Textarea
                id="edit-pipeline-description"
                value={pipelineFormData.description}
                onChange={e => setPipelineFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            {/* v2.2.0: seleção de cor da aba do pipeline (edição) */}
            <div className="space-y-2">
              <Label>{t('pipelines.color', 'Cor')}</Label>
              <div className="flex flex-wrap gap-2">
                {PIPELINE_PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      pipelineFormData.color === color && 'ring-2 ring-offset-2 ring-primary'
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setPipelineFormData(prev => ({ ...prev, color }))}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditPipelineDialogOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={confirmUpdatePipeline} disabled={updatePipelineMutation.isPending}>
              {updatePipelineMutation.isPending ? t('common.saving', 'Salvando...') : t('common.save', 'Salvar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão de Pipeline */}
      <AlertDialog open={isDeletePipelineDialogOpen} onOpenChange={setIsDeletePipelineDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pipelines.deleteTitle', 'Excluir Pipeline')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pipelines.deleteDescription', 'Tem certeza que deseja excluir o pipeline "{name}"? Esta ação não pode ser desfeita.', { name: selectedPipeline?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeletePipeline}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePipelineMutation.isPending ? t('common.deleting', 'Excluindo...') : t('common.delete', 'Excluir')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Gerenciamento de Fases */}
      <Dialog open={isPhasesDialogOpen} onOpenChange={setIsPhasesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              {t('phases.manageTitle', 'Gerenciar Fases')}
              {selectedPipelineForPhases && (
                <Badge variant="outline" className="ml-2">
                  {selectedPipelineForPhases.name}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {t('phases.manageDescription', 'Adicione, edite ou reordene as fases do pipeline.')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedPipelineForPhases && currentTenant && (
            <PipelinePhaseManager
              pipelineId={selectedPipelineForPhases.id}
              tenantId={currentTenant.id}
              onClose={() => {
                setIsPhasesDialogOpen(false);
                setSelectedPipelineForPhases(null);
                // Atualiza pipelines e stats para refletir mudanças nas fases
                queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
                queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Configuração de Webhook */}
      <WorkspaceWebhookConfig
        open={isWebhookDialogOpen}
        onOpenChange={setIsWebhookDialogOpen}
        workspace={webhookWorkspace}
      />

      {/* Dialog de Configuração de Distribuição */}
      {distributionWorkspace && (
        <Dialog open={isDistributionDialogOpen} onOpenChange={setIsDistributionDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Distribuição Automática
              </DialogTitle>
              <DialogDescription>
                Configure a distribuição automática de negócios para o workspace "{distributionWorkspace.name}"
              </DialogDescription>
            </DialogHeader>
            <WorkspaceDistributionConfig
              workspaceId={distributionWorkspace.id}
              workspaceName={distributionWorkspace.name}
              autoDistributionEnabled={distributionWorkspace.auto_distribution_enabled}
              currentQueueId={distributionWorkspace.default_queue_id}
              onSave={() => {
                setIsDistributionDialogOpen(false);
                queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Dialog de Exportação de Workspace */}
      {exportWorkspaceData && (
        <TemplateExportDialog
          open={isExportDialogOpen}
          onOpenChange={(open) => {
            setIsExportDialogOpen(open);
            if (!open) setExportWorkspaceData(null);
          }}
          onCreated={() => {
            setIsExportDialogOpen(false);
            setExportWorkspaceData(null);
          }}
          initialModules={exportSelectionModules}
          initialName={exportWorkspaceData.name}
          buildInitialSelection={buildExportSelection}
        />
      )}

      {/* Dialog de Importação de Workspace */}
      <ImportWorkspaceDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
      />

      {/* Dialog de Permissões por Fase do Workspace */}
      <WorkspacePhasePermissionsDialog
        workspace={
          permissionsWorkspace
            ? {
                id: permissionsWorkspace.id,
                name: permissionsWorkspace.name,
                phase_permissions_enabled: permissionsWorkspace.phase_permissions_enabled,
              }
            : null
        }
        open={isPermissionsDialogOpen}
        onOpenChange={(open) => {
          setIsPermissionsDialogOpen(open);
          if (!open) setPermissionsWorkspaceId(null);
        }}
      />

      {/* Dialog de Playbook de Vendas do Workspace */}
      <WorkspacePlaybooksDialog
        workspace={
          playbooksWorkspace
            ? {
                id: playbooksWorkspace.id,
                name: playbooksWorkspace.name,
                playbook_mode_enabled: playbooksWorkspace.playbook_mode_enabled,
              }
            : null
        }
        tenantId={currentTenant?.id ?? null}
        pipelines={
          playbooksWorkspace ? pipelinesMap?.[playbooksWorkspace.id] ?? [] : []
        }
        open={isPlaybooksDialogOpen}
        onOpenChange={(open) => {
          setIsPlaybooksDialogOpen(open);
          if (!open) setPlaybooksWorkspaceId(null);
        }}
      />
    </div>
  );
}
